import { Component, type CSSProperties, type ErrorInfo, type ReactNode } from 'react';
import { BAG_STORAGE_PREFIX } from '../hooks/useBag';

// Defesa em profundidade (QA CRÍTICO) — mesmo com itemValido agora validando adicionais/variacao,
// QUALQUER outro erro fatal de render (schema futuro que a validação ainda não cobre, bug de
// terceiros, etc.) derrubava a loja INTEIRA em tela branca, sem chance de recuperação: o cliente via
// uma página vazia e não tinha como saber o que houve nem como sair do estado quebrado. Este boundary
// captura o erro, mostra um fallback e oferece uma saída que também corrige a causa mais provável
// (sacola persistida corrompida): limpar todo storage de sacola (mesa + delivery, qualquer chave) e
// recarregar.
//
// O fallback é INTENCIONALMENTE estático — SEM animação de container grande: telas cheias animadas
// travam a WebView em tablet (ANR), ver lição registrada no projeto.

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log best-effort — nunca lança daqui (evitar loop de erro dentro do próprio handler de erro).
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] a loja quebrou:', error, info.componentStack);
  }

  private limparERecarregar = (): void => {
    try {
      limparSacolasPersistidas(window.localStorage);
      limparSacolasPersistidas(window.sessionStorage);
    } catch {
      // Storage indisponível (modo privado/iframe cross-origin): segue para o reload mesmo assim —
      // limpar é best-effort, nunca pode travar a recuperação.
    }
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={fallbackStyle} role="alert">
        <p style={{ margin: '0 0 16px', fontSize: 15, lineHeight: 1.5 }}>
          Algo deu errado ao carregar sua sacola. Tente limpar e recarregar a página.
        </p>
        <button type="button" onClick={this.limparERecarregar} style={buttonStyle}>
          Limpar sacola e recarregar
        </button>
      </div>
    );
  }
}

/** Remove TODA chave de sacola persistida (qualquer slug/mesa) do storage informado. */
function limparSacolasPersistidas(store: Storage): void {
  const alvos: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k && k.startsWith(BAG_STORAGE_PREFIX)) alvos.push(k);
  }
  alvos.forEach((k) => store.removeItem(k));
}

// Estilos inline (sem depender do CSS da loja — o boundary precisa funcionar mesmo se o erro tiver
// vindo de um problema no próprio CSS/tokens). Sem animação: só cor sólida + texto + botão.
const fallbackStyle: CSSProperties = {
  minHeight: '100svh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '32px 24px',
  background: '#f5f7f6',
  color: '#1b241f',
};

const buttonStyle: CSSProperties = {
  padding: '13px 22px',
  border: 0,
  borderRadius: 12,
  background: '#c1531f',
  color: '#fff',
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
};
